'use client';

/**
 * SocialEntityInspector — the dashboard-native FOCUS panel for the chain social feed.
 *
 * When a user focuses an entity (clicks a roster row or an in-feed entity chip), this
 * card leads the SAME index column — the feed never moves, and (2026-06-12) the roster
 * stays VISIBLE below this card, so focusing never interrupts discovery: the next
 * entity is one click away, no "clear first" step.
 *
 * It is intentionally a LIGHTWEIGHT feed LENS, not a mini-profile: it answers "who is this,
 * what's their pulse, who's related (RELATED leads — the door to connected entities'
 * socials), show me their posts" and then hands off to the full canonical glossary
 * profile at /tags/[slug] via "View full profile ↗". All the depth lives there; this
 * panel stays shallow so the two surfaces complement rather than duplicate.
 *
 * Gold-only dashboard tokens + the RED clear affordance (the one escape hatch earns
 * the alert color so it's findable at a glance — Lam, 2026-06-12). Reuses the embedded
 * ProfilePulse (RECON-Intel) unchanged.
 */

import { entityUrl } from '@/lib/entity-url';
import Link from 'next/link';
import { Avatar, Eyebrow, Mono, TypeBadge } from '@/components/dashboard/atoms';
import { ProfilePulse } from '@/components/admin/TwitterDashboard/ProfilePulse';
import type { EntitySearchData } from '@/types/tags';

interface RelatedTag {
  id: string;
  slug: string;
  name: string;
  twitterHandles?: string[];
}

interface SocialEntityInspectorProps {
  entity: EntitySearchData;
  selectedHandle: string;
  onHandleChange: (handle: string) => void;
  associatedTags: RelatedTag[];
  onEntitySelect: (entityId: string, entitySlug: string) => void;
  onClear: () => void;
}

export default function SocialEntityInspector({
  entity,
  selectedHandle,
  onHandleChange,
  associatedTags,
  onEntitySelect,
  onClear,
}: SocialEntityInspectorProps) {
  const handles = entity.twitterHandles ?? [];
  const pulseHandle = (selectedHandle === 'all' ? handles[0] : selectedHandle)?.toLowerCase();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface)',
        border: '0.5px solid var(--divider)',
        borderRadius: 6,
        overflow: 'hidden',
      }}
    >
      {/* Clear affordance — RED so the escape hatch is findable at a glance. */}
      <button
        onClick={onClear}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          background: 'var(--red-dim)',
          border: 'none',
          borderBottom: '0.5px solid var(--divider)',
          cursor: 'pointer',
          padding: '11px 15px',
          textAlign: 'left',
        }}
      >
        <span style={{ color: 'var(--red-text)', display: 'inline-flex', fontSize: 11, lineHeight: 1 }}>✕</span>
        <Mono size={10.5} color="var(--red-text)" weight={600}>
          Clear · back to all accounts
        </Mono>
      </button>

      {/* Entity header */}
      <div style={{ padding: '15px', borderBottom: '0.5px solid var(--divider)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {entity.primaryImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={entity.primaryImage}
              alt={entity.name}
              style={{ width: 44, height: 44, borderRadius: 6, flexShrink: 0, objectFit: 'cover', border: '0.5px solid var(--divider)' }}
            />
          ) : (
            <Avatar name={entity.name} type={entity.entityType} size={44} />
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <h2
                style={{
                  margin: 0,
                  fontFamily: 'var(--font-sans)',
                  fontSize: 17,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  color: 'var(--parchment)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {entity.name}
              </h2>
              {handles.length > 0 && (
                <span
                  aria-label="tracked"
                  style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: 'var(--green-text)' }}
                />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              {entity.entityType && <TypeBadge type={entity.entityType} />}
              <Mono size={10.5} color="var(--slate)">
                {handles.length} {handles.length === 1 ? 'account' : 'accounts'}
              </Mono>
            </div>
          </div>
        </div>
      </div>

      {/* RELATED ENTITIES — leads the card (2026-06-12): the door to connected
          entities' socials. Green dot = tracked (click focuses their feed in
          place); no dot = profile only. Buried at the bottom it read as "lost". */}
      {associatedTags.length > 0 && (
        <div style={{ padding: '13px 15px', borderBottom: '0.5px solid var(--divider)' }}>
          <Eyebrow color="var(--slate)" style={{ display: 'block', marginBottom: 9 }}>
            Related entities
          </Eyebrow>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {associatedTags.slice(0, 10).map((t) => {
              const hasHandle = (t.twitterHandles?.length ?? 0) > 0;
              return (
                <button
                  key={t.id}
                  onClick={() => onEntitySelect(t.id, t.slug)}
                  title={hasHandle ? 'Focus their social feed' : 'View profile'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    cursor: 'pointer',
                    padding: '5px 9px',
                    borderRadius: 4,
                    background: 'var(--surface-2)',
                    border: '0.5px solid var(--divider)',
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 600,
                    fontSize: 11.5,
                    color: 'var(--parchment-dim)',
                  }}
                >
                  {t.name}
                  {hasHandle && (
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--green-text)' }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Account filter (multi-handle only) */}
      {handles.length > 1 && (
        <div style={{ padding: '13px 15px', borderBottom: '0.5px solid var(--divider)' }}>
          <Eyebrow color="var(--slate)" style={{ display: 'block', marginBottom: 9 }}>
            Filter by account
          </Eyebrow>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <HandleChip label={`All (${handles.length})`} active={selectedHandle === 'all'} onClick={() => onHandleChange('all')} />
            {handles.map((h) => (
              <HandleChip key={h} label={`@${h}`} active={selectedHandle === h} onClick={() => onHandleChange(h)} />
            ))}
          </div>
        </div>
      )}

      {/* RECON Intel (embedded, reused unchanged) */}
      {pulseHandle && (
        <div style={{ borderBottom: '0.5px solid var(--divider)' }}>
          <ProfilePulse handleId={pulseHandle} variant="embedded" showWhenEmpty={false} />
        </div>
      )}

      {/* Footer actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: 13 }}>
        <Link
          href={entityUrl(entity.slug, { trailingSlash: false })}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            padding: '10px 14px',
            borderRadius: 6,
            textDecoration: 'none',
            background: 'var(--amber-dim)',
            border: '0.5px solid var(--amber-line)',
          }}
        >
          <Mono size={11.5} color="var(--amber)" weight={600}>
            View full profile ↗
          </Mono>
        </Link>
        <button
          onClick={onClear}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            padding: '10px 14px',
            borderRadius: 6,
            cursor: 'pointer',
            background: 'var(--red-dim)',
            border: '0.5px solid var(--red-line)',
          }}
        >
          <Mono size={11.5} color="var(--red-text)" weight={600}>
            ✕ Clear focus
          </Mono>
        </button>
      </div>
    </div>
  );
}

function HandleChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        cursor: 'pointer',
        fontFamily: 'var(--font-mono)',
        fontSize: 10.5,
        letterSpacing: '0.02em',
        padding: '4px 9px',
        borderRadius: 4,
        background: active ? 'var(--amber-dim)' : 'var(--surface-2)',
        border: `0.5px solid ${active ? 'var(--amber-line)' : 'var(--divider)'}`,
        color: active ? 'var(--amber)' : 'var(--slate)',
        maxWidth: 160,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
      title={label}
    >
      {label}
    </button>
  );
}
